import JsSIP from 'jssip';

import { attachReconnect } from './reconnect';
import { referBlind, type ReferBlindDeps } from './transfer';

export interface UaInput {
  wssUrl: string;
  domain: string;
  sipExtension: string;
  jwt: string;
  /** F2 staging-acceptance: SBC digest password. See shared-types SipConnectionInfo.password JSDoc. */
  password?: string;
}

export interface JsSipConfig {
  wsServers: string;
  uri: string;
  register: boolean;
  session_timers: boolean;
  display_name?: string;
  password?: string;
  authorization_user?: string;
}

export function buildJsSipConfig(input: UaInput): JsSipConfig {
  const sep = input.wssUrl.includes('?') ? '&' : '?';
  const path = input.wssUrl.includes('?') ? '' : input.wssUrl.endsWith('/') ? '' : '/';
  const wsServers = `${input.wssUrl}${path}${sep}token=${encodeURIComponent(input.jwt)}`;
  const cfg: JsSipConfig = {
    wsServers,
    uri: `sip:${input.sipExtension}@${input.domain}`,
    register: true,
    session_timers: false,
  };
  if (input.password) {
    cfg.password = input.password;
    cfg.authorization_user = input.sipExtension;
  }
  return cfg;
}

interface JsSipSessionLike {
  answer(opts?: unknown): void;
  terminate(): void;
  mute(opts: { audio?: boolean; video?: boolean }): void;
  unmute(opts: { audio?: boolean; video?: boolean }): void;
  hold(): void;
  unhold(): void;
  sendDTMF(digit: string): void;
  refer(target: string, opts: { eventHandlers: { accepted: () => void; failed: (e: { cause?: string }) => void } }): void;
  /**
   * Read-only on JsSIP RTCSession. Used as the Refer-To URI target when
   * sending REFER replaces (it's where the consult party is reachable, which
   * is what A's UA needs to dial to swap into the bridge).
   */
  remote_identity?: { uri?: { toString(): string } };
  /**
   * JsSIP RTCSession's dialog Call-ID. Used by F4.2.x cleanup to let the
   * orchestrator detect when an `onCallFailed`/`onCallEnded` event refers to
   * the consult B-leg (so it can route to MultiLegOrchestrator.onConsultFailed
   * instead of the primary-call cleanup path).
   */
  id?: string;
}

/**
 * F4.2-acceptance HF4: JsSIP's RTCSession.refer accepts an optional
 * `replaces: RTCSession` option. Our narrow JsSipSessionLike intentionally
 * omits it (the structural mismatch with the real RTCSession shape was making
 * the assignment fail under exactOptionalPropertyTypes). Instead, when we need
 * to call refer-with-replaces, we cast through this widened shape at the call
 * site only. JsSIP itself extracts call_id + to_tag + from_tag from the
 * `replaces` session internally, so we don't read those fields ourselves.
 */
interface JsSipSessionWithReplaces {
  refer(
    target: string,
    opts: {
      eventHandlers: { accepted: () => void; failed: (e: { cause?: string }) => void };
      replaces: JsSipSessionLike;
    },
  ): void;
}

export interface WrappedSession {
  answer(): void;
  hangup(): void;
  setMuted(muted: boolean): void;
  setHold(hold: boolean): void;
  sendDtmf(digit: string): void;
  refer(target: string, deps: ReferBlindDeps): Promise<void>;
  /**
   * F4.2-acceptance HF4: completes a warm transfer by sending REFER replaces
   * over THIS leg, asking the SBC to swap the far end into the bridge with
   * the consult leg. Resolves when the SBC accepts NOTIFY 200, rejects on
   * `failed` event or 10s timeout. JsSIP handles dialog-tag extraction
   * internally — we just pass the consult's WrappedSession.
   */
  referReplaces(consult: WrappedSession): Promise<void>;
  /**
   * F4.2.x cleanup #4: returns the dialog Call-ID of the underlying SIP
   * session, so callers (MultiLegOrchestrator) can match this WrappedSession
   * against the callId reported by JsSIP `failed`/`ended` events. Returns
   * undefined if the session was created outside `wrapSession` (e.g., a
   * test fake without a registry entry) or before JsSIP populated the id.
   */
  getCallId(): string | undefined;
}

/**
 * Maps a public WrappedSession back to its underlying JsSIP RTCSession so
 * that operations needing dialog-level access (REFER replaces) can find it.
 * Populated by `wrapSession`. Not exported — internal to this module.
 */
const rawSessionRegistry = new WeakMap<WrappedSession, JsSipSessionLike>();

const REFER_NOTIFY_TIMEOUT_MS = 10_000;

export function wrapSession(session: JsSipSessionLike): WrappedSession {
  const wrapped: WrappedSession = {
    answer: () => session.answer({ mediaConstraints: { audio: true, video: false } }),
    hangup: () => session.terminate(),
    setMuted: (muted) =>
      muted ? session.mute({ audio: true }) : session.unmute({ audio: true }),
    setHold: (h) => (h ? session.hold() : session.unhold()),
    sendDtmf: (d) => session.sendDTMF(d),
    refer: (target, deps) => referBlind(session, target, deps),
    getCallId: () => session.id,
    referReplaces: (consult) => {
      const consultRaw = rawSessionRegistry.get(consult);
      if (!consultRaw) {
        return Promise.reject(new Error('referReplaces: consult session not produced by wrapSession'));
      }
      const targetUri = consultRaw.remote_identity?.uri?.toString();
      if (!targetUri) {
        return Promise.reject(new Error('referReplaces: consult session lacks remote_identity.uri'));
      }
      return new Promise((resolve, reject) => {
        let settled = false;
        const settle = (fn: () => void): void => {
          if (settled) return;
          settled = true;
          clearTimeout(timeout);
          fn();
        };
        const timeout = setTimeout(() => {
          settle(() =>
            reject(
              Object.assign(new Error('SIP transfer rejected: SBC NOTIFY timeout'), {
                name: 'TransferRejectedError',
                reason: 'SBC NOTIFY timeout',
              }),
            ),
          );
        }, REFER_NOTIFY_TIMEOUT_MS);
        const sessionWithReplaces = session as unknown as JsSipSessionWithReplaces;
        sessionWithReplaces.refer(targetUri, {
          replaces: consultRaw,
          eventHandlers: {
            accepted: () => settle(() => resolve()),
            failed: (e) =>
              settle(() =>
                reject(
                  Object.assign(new Error(`SIP transfer rejected: ${e.cause ?? 'unknown'}`), {
                    name: 'TransferRejectedError',
                    reason: e.cause ?? 'unknown',
                  }),
                ),
              ),
          },
        });
      });
    },
  };
  rawSessionRegistry.set(wrapped, session);
  return wrapped;
}

interface SessionEventData {
  cause?: string;
}

interface NewRtcSessionEvent {
  session: JsSipSessionLike & {
    id: string;
    remote_identity: { uri: { user: string } };
    direction: 'incoming' | 'outgoing';
    on: (event: string, cb: (data: unknown) => void) => void;
  };
}

function extractCause(data: unknown, fallback: string): string {
  const d = data as SessionEventData;
  return d.cause ?? fallback;
}

export interface UaHandlers {
  onRegistered: () => void;
  onRegistrationFailed: (cause: string) => void;
  onDisconnected: () => void;
  onIncomingCall?: (params: { callId: string; from: string; session: WrappedSession }) => void;
  onOutgoingCallProgress?: (params: { callId: string; to: string; session: WrappedSession }) => void;
  onCallAnswered?: (params: { callId: string }) => void;
  onCallEnded?: (params: { callId: string; cause: string }) => void;
  onCallFailed?: (params: { callId: string; cause: string }) => void;
}

export function startUserAgent(
  input: UaInput,
  handlers: UaHandlers,
): { stop: () => void; placeCall: (target: string) => WrappedSession } {
  const cfg = buildJsSipConfig(input);
  const socket = new JsSIP.WebSocketInterface(cfg.wsServers);
  const ua = new JsSIP.UA({
    sockets: [socket],
    uri: cfg.uri,
    register: cfg.register,
    session_timers: cfg.session_timers,
    ...(cfg.password ? { password: cfg.password } : {}),
    ...(cfg.authorization_user ? { authorization_user: cfg.authorization_user } : {}),
  });
  ua.on('registered', handlers.onRegistered);
  ua.on('registrationFailed', (e) =>
    handlers.onRegistrationFailed(String((e as { cause: unknown }).cause)),
  );
  ua.on('disconnected', handlers.onDisconnected);
  ua.on('newRTCSession', (e: unknown) => {
    const { session } = e as NewRtcSessionEvent;
    const callId = session.id;
    const remote = session.remote_identity.uri.user;
    const wrapped = wrapSession(session);
    if (session.direction === 'incoming') {
      handlers.onIncomingCall?.({ callId, from: remote, session: wrapped });
    } else {
      handlers.onOutgoingCallProgress?.({ callId, to: remote, session: wrapped });
    }
    session.on('peerconnection', (data: unknown) => {
      const { peerconnection } = data as { peerconnection: RTCPeerConnection };
      setupIceWatchdog(peerconnection, {
        onFinalFail: () => handlers.onCallFailed?.({ callId, cause: 'ice_failed' }),
      });
    });
    session.on('accepted', () => handlers.onCallAnswered?.({ callId }));
    session.on('ended', (data) =>
      handlers.onCallEnded?.({ callId, cause: extractCause(data, 'normal') }),
    );
    session.on('failed', (data) =>
      handlers.onCallFailed?.({ callId, cause: extractCause(data, 'failed') }),
    );
  });
  ua.start();
  attachReconnect(ua, {
    backoffMs: [1_000, 2_000, 5_000, 15_000, 30_000, 60_000],
    maxAttempts: 6,
    onGiveUp: () => handlers.onDisconnected(),
  });
  return {
    stop: () => ua.stop(),
    placeCall: (target: string) => {
      const session = ua.call(`sip:${target}@${input.domain}`, {
        mediaConstraints: { audio: true, video: false },
      });
      return wrapSession(session);
    },
  };
}

export interface IceWatchdogDeps {
  onFinalFail: () => void;
  timeoutMs?: number;
}

export function setupIceWatchdog(pc: RTCPeerConnection, deps: IceWatchdogDeps): () => void {
  const timeoutMs = deps.timeoutMs ?? 10_000;
  let restarted = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const onChange = () => {
    if (pc.iceConnectionState === 'failed' && !restarted) {
      restarted = true;
      pc.restartIce();
      timer = setTimeout(() => {
        if (pc.iceConnectionState === 'failed' || pc.iceConnectionState === 'disconnected') {
          deps.onFinalFail();
        }
      }, timeoutMs);
    }
  };
  pc.addEventListener('iceconnectionstatechange', onChange);
  return () => {
    pc.removeEventListener('iceconnectionstatechange', onChange);
    if (timer) clearTimeout(timer);
  };
}
